import { createContext, useContext, useState, useEffect } from 'react'
import { hveStates, valueOrigins } from './AvmModuleConstants'
import { useAppSelector } from '../../../store/hooks'
import { selectHomePriceWizardCompletedAt } from '../../../store/selectors/home/answers'
import { selectHomeValue } from '../../../store/selectors/home/metadata'
import { selectValueOrigin } from '../../../store/selectors/home'

export interface IAvmModuleContext {
  state: {
    clientHveState: hveStates
    wizardCompletedAt?: string
    wizardInProgress?: boolean
    setWizardInProgress: (value: boolean) => void
  }
}

export const AvmModuleContext = createContext<IAvmModuleContext>({
  state: {
    clientHveState: hveStates.coreLogic,
    wizardCompletedAt: '',
    wizardInProgress: false,
    setWizardInProgress: () => {
      return
    }
  }
})

export const AvmModuleProvider = ({ children }) => {
  const [clientHveState, setClientHveState] = useState(hveStates.coreLogic)
  const [wizardInProgress, setWizardInProgress] = useState(false)
  const wizardCompletedAt = useAppSelector(selectHomePriceWizardCompletedAt)
  const homeValue = useAppSelector(selectHomeValue)
  const valueOrigin = useAppSelector(selectValueOrigin)

  useEffect(() => {
    if (homeValue === null && valueOrigin === valueOrigins.HomePriceIndex) {
      setClientHveState(hveStates.widget)
      return
    }

    if (valueOrigin === valueOrigins.Unsupported && homeValue === null) {
      setClientHveState(hveStates.unsupported)
      return
    }

    if (wizardInProgress === false) {
      if (valueOrigin === valueOrigins.CoreLogic) {
        setClientHveState(hveStates.coreLogic)
        return
      }

      if (valueOrigin === valueOrigins.HomePriceIndex || valueOrigin === valueOrigins.Unsupported) {
        setClientHveState(hveStates.hpi)
        return
      }
    }
  }, [valueOrigin, homeValue, wizardCompletedAt, wizardInProgress])

  const hveData = {
    clientHveState,
    wizardCompletedAt,
    wizardInProgress,
    setWizardInProgress
  }

  return <AvmModuleContext.Provider value={{ state: hveData }}>{children}</AvmModuleContext.Provider>
}

export const useAvmModuleContext = () => {
  const context = useContext(AvmModuleContext)

  if (!context) {
    throw new Error('useAvmModuleContext must be used within a AvmModuleContext.Provider')
  }

  return context
}
